
import React from 'react';
import styles from './_finance-trades-editor.module.scss';
import {GenericGrid, hasValue, TradeInVehicle} from "@amsanalytics/react-shared-library";
import produce from "immer";
import {TileLayout, TileLayoutItem, TilePosition} from "@progress/kendo-react-layout";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import TradeInVehicleDialog from "../../../Dialogs/TradeInVehicleDialog/TradeInVehicleDialog";
import {addNewTradeInVehicle, addNewTradeInVehicle2, updateTradeInVehicle} from "../../../../Data/VehicleData";
import { getApplicant } from '../../../../utils/appUtils';
import { Constants } from '../../../../utils/constants';
import { ApplicationDispatch, SET_LOADING } from '../../../../store/application/types';

export interface FinanceTradesEditorProps {
  currentApp: IApplication;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  openDialog: (options: { children: React.ReactNode }) => void;
  closeDialog: () => void;
  onUpdateApp: (updatedApp: IApplication) => void;
  dispatch: ApplicationDispatch;
}

export default function FinanceTradesEditor (props:  FinanceTradesEditorProps) {
  const tradeInVehicle = props.currentApp.tradeInVehicle;
  const tradeInVehicle2 = props.currentApp.tradeInVehicle2;
  const { openDialog, closeDialog } = props;
  const tradeInVehicles = [tradeInVehicle, tradeInVehicle2].filter(Boolean) as TradeInVehicle[];

  const editTradeIn = (editingVehicle?: TradeInVehicle) => {
    const isSlot2 = editingVehicle != null && tradeInVehicle2 != null
      && (editingVehicle === tradeInVehicle2 || (editingVehicle.id != null && editingVehicle.id === tradeInVehicle2.id));
    const primary = props.currentApp && getApplicant(props.currentApp, Constants.roleApplicantId, true);
    openDialog({
      children: (
        <TradeInVehicleDialog
          vehicle={editingVehicle}
          tradeInNumber={isSlot2 ? 2 : 1}
          accept={async (savedVehicle: TradeInVehicle) => {
            let newTradeInVehicle: TradeInVehicle | undefined = undefined;
            let applicationModifiedDate: string | null | undefined;
            let newAppStatusId: string | undefined;

            const slotField = isSlot2 ? 'tradeInVehicle2' : 'tradeInVehicle';
            if (!hasValue(props.currentApp[slotField]?.id)) {
                const addFn = isSlot2 ? addNewTradeInVehicle2 : addNewTradeInVehicle;
                const addResult = await addFn(props.dispatch, savedVehicle, props.currentApp.id);
                if (!addResult) return; // API failed
                newTradeInVehicle = addResult.tradeInVehicle;
                applicationModifiedDate = addResult.applicationModifiedDate;
                newAppStatusId = addResult.newAppStatusId;
            } else {
                const updateResult = await updateTradeInVehicle(props.dispatch, savedVehicle);
                if (!updateResult) return;
                applicationModifiedDate = updateResult.applicationModifiedDate;
                newAppStatusId = updateResult.newAppStatusId;
            }

            const updatedApp = produce(props.currentApp, newCurrentApp => {
              const resolved = newTradeInVehicle ?? savedVehicle;
              newCurrentApp[slotField] = resolved;

              // Keep the top-level ID in sync so FundingPage seeds the correct FK
              const idField = isSlot2 ? 'tradeInVehicle2Id' : 'tradeInVehicleId';
              newCurrentApp[idField] = resolved.id;
              if (newCurrentApp.applicationFunding) {
                // If this trade was promoted into funding slot 1 (TI1 was deleted),
                // update slot 1 rather than blindly writing to slot 2
                const funding = newCurrentApp.applicationFunding;
                if (isSlot2 && funding.tradeInVehicleId === editingVehicle?.id) {
                  funding.tradeInVehicleId = resolved.id;
                } else {
                  funding[idField] = resolved.id;
                }
              }

              // Recalculate combined totals for quotes, excluding soft-deleted trade-ins
              const t1 = isSlot2 ? props.currentApp.tradeInVehicle : resolved;
              const t2 = isSlot2 ? resolved : props.currentApp.tradeInVehicle2;
              const t1Active = t1 && !t1.deletedDate;
              const t2Active = t2 && !t2.deletedDate;
              newCurrentApp.financeQuotes.forEach(financeQuote => {
                financeQuote.tradeInPayoff = (t1Active ? (t1.tradeBalance ?? 0) : 0) + (t2Active ? (t2.tradeBalance ?? 0) : 0);
                financeQuote.tradeInValue = (t1Active ? (t1.tradeValue ?? 0) : 0) + (t2Active ? (t2.tradeValue ?? 0) : 0);
              });

              if (applicationModifiedDate) {
                newCurrentApp.modifiedDate = new Date(applicationModifiedDate);
              }

              if (hasValue(newAppStatusId)) {
                newCurrentApp.appStatusId = newAppStatusId!;
              }
            });

            props.onUpdateApp(updatedApp!);
            closeDialog();
          }}
          vehicleOfSaleStockNumber={props.currentApp.vehicle?.stockNumber}
          defaultBalance={primary?.customer?.customerIncomeDebt[0]?.priorVehicleAmountBorrowed ?? 0}
          closeDialog={closeDialog}
        />
      )
    })
  };

  const [data, setData] = React.useState<Array<TilePosition>>([
    { col: 1, colSpan: 12, rowSpan: 1, row: 1 }
  ]);

  const tiles: TileLayoutItem[] = [
    {
      defaultPosition: data[0],
      reorderable: false,
      resizable: false,
      header: 'Trades',
      body: <div className={styles.financeTradesEditorContainer}>
        <GenericGrid<TradeInVehicle>
          style={{display: 'flex', flex: 1}}
          columns={[
            {title: "VIN", field: "vin"},
            {title: "Year", field: "year"},
            {title: "Make", field: "make"},
            {title: "Model", field: "model"},
            {title: "Mileage", field: "mileage", format: "{0}"},
            {title: "Trade in Payoff", field: "payoff", format: "{0:c}"},
            {title: "Trade In Value", field: "value", format: "{0:c}"}
          ]}
          itemName={'Trade'}
          items={tradeInVehicles.filter(v => !hasValue(v.deletedDate))}
          disableAdding={true}
          disableEditing={props.financeQuoteFieldIsDisabled("tradeInVehicles")}
          disableExcelExporting={true}
          disablePdfExporting={true}
          disableFiltering={true}
          disableGrouping={true}
          onItemSelected={(item: TradeInVehicle) => { if (!props.financeQuoteFieldIsDisabled("tradeInVehicles")) editTradeIn(item); }}
          disablePaging={true}
        />
      </div>,
    }
  ];

  return (
    <TileLayout
      columns={12}
      className={styles.quoteEditorTileLayout}
      gap={{ rows: 10, columns: 10 }}
      rowHeight={"100%"}
      // style={{display: 'flex', flex: 1}}
      style={{ width: '100%'}}
      positions={data}
      items={tiles}
    />
  );
}
